<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { fromUnixTime, isSameYear, isToday, isTomorrow } from 'date-fns';
import { messageTimeOfDay, standardDateTime } from 'shared/helpers/timeHelper';
import { renderTemplatePreview } from 'dashboard/helper/templateHelper';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';

import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  scheduledMessage: { type: Object, required: true },
});

const emit = defineEmits(['cancel']);

const { t, locale } = useI18n();

const templateName = computed(
  () => props.scheduledMessage.template_params?.name
);

const formattedContent = computed(() => {
  const { content, template_params: templateParams } = props.scheduledMessage;
  const text = renderTemplatePreview(
    content,
    templateParams?.processed_params?.body || {}
  );
  return new MessageFormatter(text, false, false, true, true).formattedMessage;
});

const scheduledLabel = computed(() => {
  const { scheduled_at: scheduledAt } = props.scheduledMessage;
  const date = fromUnixTime(scheduledAt);
  const time = messageTimeOfDay(scheduledAt, locale.value);

  if (isToday(date)) return t('SCHEDULED_MESSAGES.THREAD.TODAY_AT', { time });
  if (isTomorrow(date)) {
    return t('SCHEDULED_MESSAGES.THREAD.TOMORROW_AT', { time });
  }

  const day = new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(isSameYear(date, new Date()) ? {} : { year: 'numeric' }),
  }).format(date);
  return t('SCHEDULED_MESSAGES.THREAD.DAY_AT', { day, time });
});

const fullTimestamp = computed(() =>
  standardDateTime(props.scheduledMessage.scheduled_at)
);

// It goes out as the agent who scheduled it, so the placeholder carries that agent's avatar the
// way the sent message will.
const avatarInfo = computed(() => {
  const { user } = props.scheduledMessage;
  if (!user) return { name: '', iconName: 'i-lucide-clock' };
  return { name: user.available_name || user.name, src: user.thumbnail };
});

const avatarTooltip = computed(() =>
  avatarInfo.value.name
    ? t('SCHEDULED_MESSAGES.THREAD.SCHEDULED_BY', {
        name: avatarInfo.value.name,
      })
    : ''
);
</script>

<template>
  <li
    class="flex gap-2 justify-end items-end mb-2 w-full list-none group/scheduled"
  >
    <div
      class="flex relative flex-col gap-2 px-4 py-3 min-w-0 max-w-lg text-sm rounded-xl border border-dashed ltr:ml-8 rtl:mr-8 ltr:rounded-br-sm rtl:rounded-bl-sm border-n-blue-8 bg-n-solid-blue/50 text-n-slate-12"
    >
      <span
        v-if="templateName"
        class="flex gap-1 items-center text-xs text-n-slate-11"
      >
        <Icon icon="i-lucide-layout-template" class="size-3" />
        {{
          t('SCHEDULED_MESSAGES.THREAD.TEMPLATE_NAME', { name: templateName })
        }}
      </span>
      <span v-dompurify-html="formattedContent" class="prose prose-bubble" />
      <div
        class="flex flex-wrap gap-1.5 justify-end items-center text-xs text-n-slate-11"
      >
        <Icon icon="i-lucide-clock" class="size-3" />
        <time v-tooltip.top="fullTimestamp">{{ scheduledLabel }}</time>
        <span v-if="scheduledMessage.reopen_conversation">
          {{ t('SCHEDULED_MESSAGES.THREAD.WILL_REOPEN') }}
        </span>
      </div>
      <Button
        v-tooltip.top="t('SCHEDULED_MESSAGES.THREAD.CANCEL')"
        icon="i-lucide-x"
        slate
        solid
        xs
        class="absolute top-1 opacity-0 transition-opacity duration-100 ltr:right-1 rtl:left-1 focus:opacity-100 group-hover/scheduled:opacity-100"
        :aria-label="t('SCHEDULED_MESSAGES.THREAD.CANCEL')"
        @click="emit('cancel')"
      />
    </div>
    <Avatar v-tooltip.left-end="avatarTooltip" v-bind="avatarInfo" :size="24" />
  </li>
</template>
